import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Clock, AlertTriangle, FileText, Link2, GitBranch, Upload, FileEdit } from "lucide-react";
import { Loading } from "@/pages/_shared";
import api from "@/lib/api";

function Stat({ label, value, icon: Icon, tone = "slate", testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="text-[13px] font-medium text-slate-500">{label}</span>
        <Icon size={18} className={tone === "amber" ? "text-amber-500" : tone === "red" ? "text-red-500" : "text-slate-400"} />
      </div>
      <div className="mt-2 font-head text-3xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

export default function OwnerOverview() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/mission-control/overview").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data) return <Loading />;
  const sys = data.system || {};
  const queues = data.queues || [];
  const openIssues = queues.filter((q) => !["healthy", "idle"].includes((q.state || "").toLowerCase())).length;
  const docQueue = queues.find((q) => /document/i.test(q.name));

  const attention = [
    docQueue && { icon: FileText, title: "Document reviews", sub: `${docQueue.depth} document${docQueue.depth === 1 ? "" : "s"} waiting for review`, cta: "Review", to: "/owner/approvals" },
    { icon: Link2, title: "Integration connection", sub: "Stripe connection needs attention", cta: "Manage", to: "/owner/integrations" },
    { icon: GitBranch, title: "Workflow exception", sub: "1 workflow has failed", cta: "Inspect", to: "/owner/ai-workforce" },
  ].filter(Boolean);

  const activity = (data.audit || []).slice(0, 6).map((a) => ({
    icon: /upload|document|received/i.test(a.action) ? Upload : FileEdit,
    text: a.action, when: a.when,
  }));

  return (
    <div className="space-y-6 animate-fade-up" data-testid="owner-overview-page">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Customers" value={sys.total_members ?? "—"} icon={Users} testid="stat-customers" />
        <Stat label="Pending approvals" value={sys.pending_approvals ?? 0} icon={Clock} tone="amber" testid="stat-approvals" />
        <Stat label="Open issues" value={openIssues} icon={AlertTriangle} tone="red" testid="stat-issues" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Needs your attention</h2>
        <div className="mt-4 divide-y divide-slate-100">
          {attention.map((it, i) => (
            <div key={it.title} className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0" data-testid="attention-row">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><it.icon size={18} /></span>
                <div>
                  <div className="font-head text-[14px] font-bold text-slate-900">{it.title}</div>
                  <div className="text-[13px] text-slate-500">{it.sub}</div>
                </div>
              </div>
              <button onClick={() => navigate(it.to)} data-testid="attention-cta"
                className="shrink-0 rounded-xl bg-[#1a5fd0] px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#1550b8]">
                {it.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Recent activity</h2>
        <div className="mt-3 divide-y divide-slate-100">
          {activity.length === 0 && <div className="py-4 text-[13px] text-slate-400">No recent activity.</div>}
          {activity.map((a, i) => (
            <div key={`${a.text}-${a.when}-${i}`} className="flex items-center gap-3 py-3" data-testid="activity-row">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><a.icon size={15} /></span>
              <span className="flex-1 truncate text-[14px] text-slate-700">{a.text}</span>
              <span className="shrink-0 font-mono text-[11px] text-slate-400">{String(a.when).length > 10 ? String(a.when).slice(0, 10) : a.when}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
